import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { type VersionHistoryEntry, VersionHistoryMenu } from "../../../components/UIToolkit/Popups/VersionHistoryMenu";
import type { CustomPrintoutVersionSummary } from "./customPrintoutTypes";
import { useCustomPrintoutVersionMeta } from "./useCustomPrintoutVersionMeta";
import type { CustomPrintoutVersionActions } from "./useCustomPrintoutVersionView";

type Props = {
	versions: CustomPrintoutVersionActions;
	/** The version on screen: the earlier one being viewed, else the current one. */
	shownVersionNumber: number;
	/** The earlier version on screen, or `null` while the current one is. */
	viewedVersionId: string | null;
};

/** The id the menu marks as on screen: the viewed earlier version, else whichever version is current. */
const shownIdOf = (rows: CustomPrintoutVersionSummary[] | undefined, viewedVersionId: string | null): string | null => {
	if (viewedVersionId !== null) return viewedVersionId;
	const current = rows?.find((row) => row.isCurrent);
	return current === undefined ? null : current.id;
};

/** The shown printout's version history, opened from the chip under the printout list. */
export const CustomPrintoutVersionHistory: FC<Props> = ({ versions, shownVersionNumber, viewedVersionId }) => {
	const { t } = useTranslation();
	const metaOf = useCustomPrintoutVersionMeta();
	const rows = versions.versionsQuery.data;
	const entries = rows?.map(
		(row): VersionHistoryEntry => ({
			id: row.id,
			title: t("printout.custom.ai.version.number", { number: row.versionNumber }),
			summary: row.summary,
			meta: metaOf(row),
			note: row.restoredFromVersionNumber === null ? undefined : t("printout.custom.ai.version.restored.from", { number: row.restoredFromVersionNumber }),
			badge: row.isCurrent ? t("printout.custom.ai.version.current") : undefined,
			isLive: row.isCurrent,
			dataTestId: `custom-printout-version-${row.versionNumber}`,
		}),
	);
	return (
		<VersionHistoryMenu
			entries={entries}
			status={versions.versionsQuery.status}
			shownId={shownIdOf(rows, viewedVersionId)}
			onSelect={versions.view}
			onOpenChange={versions.onHistoryOpenChange}
			triggerLabel={t("printout.custom.ai.version.short", { number: shownVersionNumber })}
			triggerAriaLabel={t("printout.custom.ai.version.history.trigger", { number: shownVersionNumber })}
			title={t("printout.custom.ai.version.history")}
			hint={t("printout.custom.ai.version.history.hint")}
			errorLabel={t("printout.custom.ai.version.history.failed")}
			onRetry={versions.retryHistory}
			active={viewedVersionId !== null}
			dataTestId="custom-printout-versions"
		/>
	);
};
